import { useState } from 'react';

import { useMutation } from '@apollo/client/react';
import { IconButton, Menu, MenuItem } from '@mui/material';
import { useColorScheme } from '@mui/material/styles';
import { refreshToken } from '@realadvisor/auth-client';
import { Check, Globe, Monitor } from 'lucide-react';

import { LanguageMenuUpdateUserDocument } from '@graphql/__generated__/graphql';
import RASnackbar from '@modules/_shared/components/RASnackbar';
import { useLocale } from '@shared/hooks/locale';
import type { Language } from '@shared/hooks/locale';
import {
  languageMenuOptions,
  replacePathnameLanguage,
} from '@shared/LocaleContext';
import { getThemeMenuOptions } from '@shared/theme/themeMenuOptions';

import { getOnboardingUserIdFromJwt } from './onboardingJwt';

const triggerSx = {
  flexShrink: 0,
  border: 1,
  borderColor: 'divider',
  borderRadius: '10px',
  color: 'text.primary',
  bgcolor: 'rgba(var(--mui-palette-background-paperChannel) / 0.85)',
  backdropFilter: 'blur(8px)',
  '&:hover': {
    bgcolor: 'rgba(var(--mui-palette-primary-mainChannel) / 0.08)',
  },
} as const;

const menuPaperSlotProps = {
  paper: {
    elevation: 8,
    sx: { borderRadius: '12px', minWidth: 200 },
  },
} as const;

const LanguageMenu = ({
  onBeforeLanguageChange,
}: {
  onBeforeLanguageChange?: () => boolean;
}) => {
  const { t, language } = useLocale();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [languageError, setLanguageError] = useState<string | null>(null);
  const open = Boolean(anchorEl);
  const [updateUserLanguage, { loading: languageUpdating }] = useMutation(
    LanguageMenuUpdateUserDocument,
  );

  const changeLanguage = async (newLanguage: Language) => {
    setAnchorEl(null);
    if (language === newLanguage) {
      return;
    }
    setLanguageError(null);
    const pathname = replacePathnameLanguage(
      window.location.pathname,
      newLanguage,
    );
    const nextUrl = `${pathname}${window.location.search}${window.location.hash}`;
    const userId = getOnboardingUserIdFromJwt();
    if (userId === '') {
      if (onBeforeLanguageChange?.() === false) {
        setLanguageError(t('Something went wrong. Please try again.'));
        return;
      }
      window.location.assign(nextUrl);
      return;
    }
    try {
      await updateUserLanguage({
        variables: { id: userId, language: newLanguage },
      });
    } catch (error) {
      console.error('[onboarding] failed to save language preference', error);
      setLanguageError(t('Something went wrong. Please try again.'));
      return;
    }
    try {
      await refreshToken();
    } catch (error) {
      console.warn(
        '[onboarding] failed to refresh token after saving language preference',
        error,
      );
    }
    if (onBeforeLanguageChange?.() === false) {
      setLanguageError(t('Something went wrong. Please try again.'));
      return;
    }
    window.location.assign(nextUrl);
  };

  return (
    <>
      <IconButton
        sx={triggerSx}
        title={t('Language')}
        onClick={event => setAnchorEl(event.currentTarget)}
      >
        <Globe size={22} strokeWidth={1.8} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={menuPaperSlotProps}
      >
        {languageMenuOptions.map(option => {
          const active = language === option.value;
          return (
            <MenuItem
              key={option.value}
              onClick={() => void changeLanguage(option.value)}
              disabled={languageUpdating}
              sx={{
                justifyContent: 'space-between',
                gap: 2,
                color: active ? 'primary.main' : 'text.primary',
                bgcolor: active
                  ? 'rgba(var(--mui-palette-primary-mainChannel) / 0.08)'
                  : 'transparent',
              }}
            >
              {option.label}
              {active && <Check size={16} strokeWidth={2} />}
            </MenuItem>
          );
        })}
      </Menu>
      {languageError && (
        <RASnackbar
          message={languageError}
          onClose={() => setLanguageError(null)}
        />
      )}
    </>
  );
};

const ThemeMenu = () => {
  const { t } = useLocale();
  const { mode, setMode } = useColorScheme();
  const themeMode = mode ?? 'system';
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const themeOptions = getThemeMenuOptions(t);

  const ActiveIcon =
    themeOptions.find(option => option.value === themeMode)?.Icon ?? Monitor;

  const changeMode = (value: (typeof themeOptions)[number]['value']) => {
    setMode(value);
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton
        sx={triggerSx}
        title={t('Theme')}
        onClick={event => setAnchorEl(event.currentTarget)}
      >
        <ActiveIcon size={22} strokeWidth={1.8} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={menuPaperSlotProps}
      >
        {themeOptions.map(option => {
          const active = themeMode === option.value;
          return (
            <MenuItem
              key={option.value}
              onClick={() => changeMode(option.value)}
              sx={{
                gap: 1.5,
                color: active ? 'primary.main' : 'text.primary',
                bgcolor: active
                  ? 'rgba(var(--mui-palette-primary-mainChannel) / 0.08)'
                  : 'transparent',
              }}
            >
              <option.Icon size={18} strokeWidth={1.8} />
              {option.label}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export { LanguageMenu, ThemeMenu };
